import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import * as Models from "app/services/proxy/model/models";
import {Lookup, LookupHelpers, UtilsService} from "framework/services/utils.service";
import {ControlOption} from "framework/ui/control-option";
import {inParallel} from "framework/utils/concurrency";

import {ResetMemoizers} from "framework/utils/memoizers";

@Injectable()
export class WorkloadQueryService
{
    constructor(public api: ApiService)
    {}

    public async regionFiltersData(cloud_accounts: string[]): Promise<Models.WorkloadServiceResourceRegion[]>
    {
        const regions = await this.api.workloadQueryApi.resourceFiltersRegion(Models.WorkloadServiceResourceRegionRequest.newInstance({cloud_account_ids: cloud_accounts}));
        if (!regions) return [];
        return regions.sort((a,
                             b) => UtilsService.compareStrings(a.region, b.region, true));
    }

    public async resourceGroupsFiltersData(cloud_accounts: string[]): Promise<Models.ResourceGroupDetailsBase[]>
    {
        const resourceGroups: Models.ResourceGroupDetailsBase[] = [];

        await inParallel(cloud_accounts, async (account) =>
        {
            try
            {
                const resourceGroupResponse = await this.api.serviceAccountGovernanceApi.listAvailableResourceGroups(account);
                resourceGroups.push(...resourceGroupResponse);
            }
            catch
            {
                // Ignoring all failures
            }
        });

        return resourceGroups.sort((a,
                                    b) => UtilsService.compareStrings(a.name, b.name, true));
    }

    public serviceResourceFiltersData(serviceAccountIds: string[]): Promise<Models.WorkloadServiceResource[]>
    {
        const req = Models.WorkloadServiceResourceRequest.newInstance({service_account_ids: serviceAccountIds});
        return this.api.workloadQueryApi.resourceFiltersServiceResource(req);
    }

    public async serviceResourceTagFiltersData(serviceAccountIds: string[]): Promise<Models.WorkloadServiceResource[]>
    {
        const data = await this.serviceResourceFiltersData(serviceAccountIds);
        return data?.filter((r) => r.supports_tags) || [];
    }

    public async getFilterOptions<T extends CloudAssociated>(filterInfo: T[],
                                                             getRelevantInfo: (data: T) => string,
                                                             cloudProviders: Set<Models.CloudServiceName>): Promise<ControlOption<string>[]>
    {
        const cloudOrganized: Lookup<Set<string>> = {};
        for (let info of filterInfo)
        {
            if (!cloudProviders || cloudProviders.has(info.cloud))
            {
                const ofSameCloud = LookupHelpers.ensureBaseState(cloudOrganized, info.cloud, () => new Set());
                ofSameCloud.add(getRelevantInfo(info));
            }
        }

        let options: ControlOption<string>[] = [];
        for (const cloud in cloudOrganized)
        {
            const parent = new ControlOption(cloud, cloud.replace("_", " "), [], true);
            options.push(parent);

            for (const info of cloudOrganized[cloud])
            {
                if (!info) continue;
                parent.children.push(new ControlOption(info, info));
            }
            parent.children.sort((a,
                                  b) => UtilsService.compareStrings(a.label, b.label, true));
        }

        if (options.length === 1) options = options[0].children;

        return options;
    }

    @ResetMemoizers
    public resetData()
    {}
}

export interface CloudAssociated
{
    cloud: Models.CloudServiceName;
}
